import React, { useState, useRef } from 'react';
import { Tag } from '../types';

interface TagInputProps {
  selectedTags: Tag[];
  onTagsChange: (tags: Tag[]) => void;
  availableTags: Tag[];
  onNewTagCreated?: (tag: Tag) => void;
  disabled?: boolean;
  placeholder?: string;
  // New props to support pending tag names
  selectedTagNames?: string[];
  onTagNamesChange?: (tagNames: string[]) => void;
}

const TagInput: React.FC<TagInputProps> = ({
  selectedTags,
  onTagsChange,
  availableTags,
  onNewTagCreated,
  disabled = false,
  placeholder = "Type to add tags...",
  selectedTagNames = [],
  onTagNamesChange
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter available tags based on input and exclude already selected
  const filteredTags = availableTags.filter(tag =>
    !selectedTags.some(selected => selected.id === tag.id) &&
    tag.name.toLowerCase().includes(inputValue.toLowerCase())
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setShowDropdown(value.length > 0 && filteredTags.length > 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();

      if (inputValue.trim()) {
        addTag(inputValue.trim());
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setShowDropdown(false);
      setInputValue('');
    } else if (e.key === 'Backspace' && inputValue === '') {
      // Remove last item when backspacing on empty input
      e.preventDefault();
      if (selectedTagNames.length > 0 && onTagNamesChange) {
        // Remove last pending tag name
        onTagNamesChange(selectedTagNames.slice(0, -1));
      } else if (selectedTags.length > 0) {
        // Remove last selected tag
        removeTag(selectedTags[selectedTags.length - 1]);
      }
    }
  };

  const addTag = (tagName: string) => {
    if (disabled) return;

    // Check if tag already exists in available tags
    const existingTag = availableTags.find(tag =>
      tag.name.toLowerCase() === tagName.toLowerCase()
    );

    if (existingTag) {
      // Add existing tag if not already selected
      if (!selectedTags.some(selected => selected.id === existingTag.id)) {
        onTagsChange([...selectedTags, existingTag]);
      }
    } else {
      // Add new tag name to pending list if onTagNamesChange is provided
      if (onTagNamesChange) {
        // Check if tag name not already in pending list
        if (!selectedTagNames.some(name => name.toLowerCase() === tagName.toLowerCase())) {
          onTagNamesChange([...selectedTagNames, tagName]);
        }
      } else {
        // Fallback to the old behavior if not using the new props
        console.warn('New tag creation not supported without onTagNamesChange prop');
      }
    }

    setInputValue('');
    setShowDropdown(false);
  };

  const removeTag = (tagToRemove: Tag) => {
    if (disabled) return;
    onTagsChange(selectedTags.filter(tag => tag.id !== tagToRemove.id));
  };

  const selectFromDropdown = (tag: Tag) => {
    if (!selectedTags.some(selected => selected.id === tag.id)) {
      onTagsChange([...selectedTags, tag]);
    }
    setInputValue('');
    setShowDropdown(false);
    inputRef.current?.focus();
  };


  const handleInputFocus = () => {
    if (inputValue.length > 0 && filteredTags.length > 0) {
      setShowDropdown(true);
    }
  };

  const handleInputBlur = () => {
    // Delay hiding dropdown to allow click on dropdown items
    setTimeout(() => setShowDropdown(false), 150);
  };

  return (
    <div className="position-relative">
      <div className={`form-control d-flex flex-wrap align-items-center gap-2 ${disabled ? 'bg-light' : ''}`}
           style={{ minHeight: '42px', cursor: disabled ? 'not-allowed' : 'text' }}
           onClick={() => !disabled && inputRef.current?.focus()}>

        {/* Selected Tags */}
        {selectedTags.map(tag => (
          <span
            key={tag.id}
            className="badge bg-secondary d-inline-flex align-items-center gap-1"
            style={{
              fontSize: '0.75rem'
            }}
          >
            {tag.name}
            {!disabled && (
              <button
                type="button"
                className="btn-close btn-close-white"
                style={{ fontSize: '0.6rem' }}
                onClick={(e) => {
                  e.stopPropagation();
                  removeTag(tag);
                }}
                aria-label={`Remove ${tag.name} tag`}
              />
            )}
          </span>
        ))}

        {/* Pending Tag Names */}
        {selectedTagNames.map((tagName, index) => (
          <span
            key={`pending-${index}`}
            className="badge bg-info d-inline-flex align-items-center gap-1"
            style={{
              fontSize: '0.75rem'
            }}
          >
            {tagName} (new)
            {!disabled && onTagNamesChange && (
              <button
                type="button"
                className="btn-close btn-close-white"
                style={{ fontSize: '0.6rem' }}
                onClick={(e) => {
                  e.stopPropagation();
                  onTagNamesChange(selectedTagNames.filter((_, i) => i !== index));
                }}
                aria-label={`Remove ${tagName} tag`}
              />
            )}
          </span>
        ))}

        {/* Input Field */}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          disabled={disabled}
          placeholder={selectedTags.length === 0 && selectedTagNames.length === 0 ? placeholder : ''}
          className="border-0 outline-0 flex-grow-1"
          style={{
            background: 'transparent',
            minWidth: '120px',
            boxShadow: 'none'
          }}
          autoComplete="off"
          data-form-type="other"
        />

      </div>

      {/* Dropdown for existing tags */}
      {showDropdown && filteredTags.length > 0 && (
        <div className="dropdown-menu show position-absolute w-100" style={{ zIndex: 1050 }}>
          {filteredTags.slice(0, 8).map(tag => (
            <button
              key={tag.id}
              type="button"
              className="dropdown-item d-flex align-items-center gap-2"
              onClick={() => selectFromDropdown(tag)}
            >
              <span
                className="badge bg-secondary"
                style={{
                  minWidth: '8px',
                  height: '8px',
                  borderRadius: '50%'
                }}
              ></span>
              {tag.name}
            </button>
          ))}
        </div>
      )}

      {/* Help text */}
      <small className="form-text text-muted mt-1" style={{ fontSize: '0.75rem' }}>
        Type tag name and press Enter to add. {!disabled && onTagNamesChange && 'New tags will be created when saving the customer.'}
      </small>
    </div>
  );
};

export default TagInput;
